import React, { useState } from 'react';
import './QuestionImport.css';

const QuestionImport = ({ onImport, onCancel, initialJsonData = '' }) => {
  const [jsonText, setJsonText] = useState(initialJsonData);
  const [file, setFile] = useState(null);
  const [importMethod, setImportMethod] = useState('text'); // 'text' or 'file'
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const sampleJson = `[
  {
    "questionText": "JavaScriptの変数宣言に使用するキーワードはどれですか？",
    "questionType": "single",
    "tags": ["JavaScript", "変数", "基礎"],
    "choices": [
      {"id": "a", "text": "var", "isCorrect": true},
      {"id": "b", "text": "string", "isCorrect": false},
      {"id": "c", "text": "function", "isCorrect": false},
      {"id": "d", "text": "return", "isCorrect": false}
    ],
    "explanation": "JavaScriptでは、var、let、constが変数宣言に使用されます。"
  },
  {
    "questionText": "以下のうち、配列のメソッドはどれですか？（複数選択）",
    "questionType": "multiple",
    "tags": ["JavaScript", "配列", "メソッド"],
    "choices": [
      {"id": "a", "text": "push()", "isCorrect": true},
      {"id": "b", "text": "pop()", "isCorrect": true},
      {"id": "c", "text": "alert()", "isCorrect": false},
      {"id": "d", "text": "slice()", "isCorrect": true}
    ],
    "explanation": "push()、pop()、slice()は配列のメソッドです。alert()はウィンドウオブジェクトのメソッドです。"
  }
]`;

  const validateQuestion = (question, index) => {
    const errors = [];
    
    // Required fields
    if (!question.questionText || typeof question.questionText !== 'string') {
      errors.push(`質問 ${index + 1}: questionText が必要です`);
    }
    
    if (!question.questionType || !['single', 'multiple'].includes(question.questionType)) {
      errors.push(`質問 ${index + 1}: questionType は 'single' または 'multiple' である必要があります`);
    }
    
    if (!Array.isArray(question.choices) || question.choices.length < 2) {
      errors.push(`質問 ${index + 1}: choices は最低2つの選択肢が必要です`);
    }
    
    // Validate choices
    if (Array.isArray(question.choices)) {
      const correctChoices = question.choices.filter(choice => choice.isCorrect);
      
      if (correctChoices.length === 0) {
        errors.push(`質問 ${index + 1}: 最低1つの正解が必要です`);
      }
      
      if (question.questionType === 'single' && correctChoices.length > 1) {
        errors.push(`質問 ${index + 1}: 単一選択では正解は1つだけです`);
      }
      
      question.choices.forEach((choice, choiceIndex) => {
        if (!choice.id || typeof choice.id !== 'string') {
          errors.push(`質問 ${index + 1}, 選択肢 ${choiceIndex + 1}: id が必要です`);
        }
        if (!choice.text || typeof choice.text !== 'string') {
          errors.push(`質問 ${index + 1}, 選択肢 ${choiceIndex + 1}: text が必要です`);
        }
        if (typeof choice.isCorrect !== 'boolean') {
          errors.push(`質問 ${index + 1}, 選択肢 ${choiceIndex + 1}: isCorrect はboolean型である必要があります`);
        }
      });
    }
    
    // Optional fields validation
    if (question.tags && !Array.isArray(question.tags)) {
      errors.push(`質問 ${index + 1}: tags は配列である必要があります`);
    }
    
    if (question.explanation && typeof question.explanation !== 'string') {
      errors.push(`質問 ${index + 1}: explanation は文字列である必要があります`);
    }
    
    return errors;
  };

  const validateJsonData = (data) => {
    const errors = [];
    
    if (!Array.isArray(data)) {
      errors.push('データは配列である必要があります');
      return errors;
    }
    
    if (data.length === 0) {
      errors.push('最低1つの質問が必要です');
      return errors;
    }
    
    data.forEach((question, index) => {
      errors.push(...validateQuestion(question, index));
    });
    
    return errors;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/json') {
        setValidationError('JSONファイルを選択してください');
        return;
      }
      
      setFile(selectedFile);
      setValidationError('');
      
      // Read file content
      const reader = new FileReader();
      reader.onload = (event) => {
        setJsonText(event.target.result);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleTextChange = (e) => {
    setJsonText(e.target.value);
    setValidationError('');
  };

  const handleValidateAndImport = async () => {
    if (!jsonText.trim()) {
      setValidationError('JSONデータを入力してください');
      return;
    }
    
    setIsValidating(true);
    setValidationError('');
    
    try {
      const parsedData = JSON.parse(jsonText);
      const errors = validateJsonData(parsedData);
      
      if (errors.length > 0) {
        setValidationError(errors.join('\n'));
        setIsValidating(false);
        return;
      }
      
      // Add order to questions
      const questionsWithOrder = parsedData.map((question, index) => ({
        ...question,
        order: index + 1,
        questionImageUrl: question.questionImageUrl || null,
        explanationImageUrl: question.explanationImageUrl || null,
        tags: question.tags || []
      }));
      
      await onImport(questionsWithOrder);
      setIsValidating(false);
      
    } catch (error) {
      setValidationError(`JSON解析エラー: ${error.message}`);
      setIsValidating(false);
    }
  };

  const handleUseSample = () => {
    setJsonText(sampleJson);
    setValidationError('');
  };


  return (
    <div className="question-import">
      <div className="question-import-header">
        <h2>質問をインポート</h2>
        <button onClick={onCancel} className="cancel-btn">
          ← 戻る
        </button>
      </div>

      <div className="import-content">
        <div className="import-method-selector">
          <label>
            <input
              type="radio"
              value="text"
              checked={importMethod === 'text'}
              onChange={(e) => setImportMethod(e.target.value)}
            />
            テキスト入力
          </label>
          <label>
            <input
              type="radio"
              value="file"
              checked={importMethod === 'file'}
              onChange={(e) => setImportMethod(e.target.value)}
            />
            ファイルアップロード
          </label>
        </div>

        {importMethod === 'file' && (
          <div className="file-upload-section">
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="file-input"
            />
            {file && (
              <div className="file-info">
                選択されたファイル: {file.name}
              </div>
            )}
          </div>
        )}

        <div className="json-input-section">
          <div className="json-input-header">
            <label>JSON データ</label>
            <div className="json-input-buttons">
              <button onClick={handleUseSample} className="sample-btn">
                サンプルを使用
              </button>
            </div>
          </div>
          <textarea
            value={jsonText}
            onChange={handleTextChange}
            placeholder="JSONデータを入力してください..."
            className="json-textarea"
            rows="15"
          />
        </div>

        {validationError && (
          <div className="validation-error">
            <pre>{validationError}</pre>
          </div>
        )}


        <div className="import-instructions">
          <h3>JSONフォーマット</h3>
          <div className="format-description">
            <p>以下の形式で質問を配列として入力してください：</p>
            <ul>
              <li><strong>questionText</strong>: 質問文（必須）</li>
              <li><strong>questionType</strong>: "single" または "multiple"（必須）</li>
              <li><strong>choices</strong>: 選択肢の配列（必須、最低2つ）</li>
              <li><strong>tags</strong>: タグの配列（任意）</li>
              <li><strong>explanation</strong>: 解説文（任意）</li>
            </ul>
            <p>各選択肢は id、text、isCorrect を含む必要があります。</p>
          </div>
        </div>

        <div className="import-actions">
          <button onClick={onCancel} className="cancel-btn">
            キャンセル
          </button>
          <button 
            onClick={handleValidateAndImport} 
            className="import-btn"
            disabled={isValidating}
          >
            {isValidating ? '検証中...' : 'インポート'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionImport;